import { useState } from 'react';
import { useNumbers, useLabelNumber } from '../../lib/api/hooks-tms.js';
import { useCan } from '../../lib/auth/session.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, StatusBadge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * The account's numbers, and the inbound route each one is on.
 *
 * Read-only for routing. A number is routed by putting it on a route under
 * Inbound routes, where one decision covers every number that shares it
 * (docs/26 D1). A number on no route is flagged here because calls to it are
 * refused -- still the commonest cause of "our new number does not work".
 *
 * The name is the account's own ("Main line", "Durban"), so fifty numbers can be told apart in
 * route pickers without reading digits (0098).
 */
export function NumbersTab({ tenantId, onNavigate }: {
  tenantId: string; onNavigate?: (section: string) => void;
}) {
  const numbers = useNumbers(tenantId);
  const rows = numbers.data?.data ?? [];
  const unrouted = rows.filter((n) => !n.route).length;
  const can = useCan();
  const writable = can('inboundroute:write');

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Numbers</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            {rows.length} {rows.length === 1 ? 'number' : 'numbers'}
            {unrouted > 0 ? ` · ${unrouted} on no route` : ''}
          </p>
        </div>
        <div className="arc-spacer" />
        {onNavigate && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => onNavigate('inbound')}>
            Inbound routes
          </button>
        )}
      </div>

      {numbers.error && <ErrorBanner error={numbers.error} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Number</th><th>Name</th><th>Type</th><th>Carrier</th><th>Status</th><th>Inbound route</th>
              </tr>
            </thead>
            {numbers.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {rows.map((n) => (
                  <tr key={n.id}>
                    <td className="arc-mono" style={{ fontWeight: 600 }}>{n.e164}</td>
                    <td>
                      <NumberName tenantId={tenantId} id={n.id} label={n.label ?? null}
                        writable={writable} />
                    </td>
                    <td className="arc-subtle">{n.number_type.replace(/_/g, ' ')}</td>
                    <td className="arc-muted">{n.carrier_name ?? '—'}</td>
                    <td><StatusBadge status={n.status} /></td>
                    <td>
                      {n.route ? (
                        <span>
                          {n.route.name}
                          {!n.route.is_enabled && <> <Badge tone="warn">off</Badge></>}
                        </span>
                      ) : (
                        <span className="arc-badge arc-badge-warn">
                          <Icon name="warning" size={10} /> no route
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!numbers.isLoading && rows.length === 0 && (
          <Empty icon="hash" title="No numbers assigned"
            hint="Assign a DID from platform inventory to give this account an inbound number." />
        )}
      </div>
    </>
  );
}

/** A number's name, edited where it is shown: click, type, Enter. Escape leaves it as it was. */
function NumberName({ tenantId, id, label, writable }: {
  tenantId: string; id: string; label: string | null; writable: boolean;
}) {
  const save = useLabelNumber(tenantId);
  const [draft, setDraft] = useState<string | null>(null);

  if (!writable) return label ? <span>{label}</span> : <span className="arc-subtle">—</span>;

  if (draft === null) {
    return (
      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setDraft(label ?? '')}
        title="Rename">
        {label ?? <span className="arc-subtle">Add name</span>}
      </button>
    );
  }

  const commit = () => {
    const next = draft.trim() || null;
    setDraft(null);
    if (next !== label) save.mutate({ id, label: next });
  };

  return (
    <input
      className="arc-input" style={{ maxWidth: 200, height: 28 }} autoFocus maxLength={60}
      placeholder="Main line" value={draft} aria-label="Number name"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setDraft(null);
      }}
    />
  );
}
